'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SWIPE } from '@/constants';
import { cn } from '@/utils/cn';
import { SORT_BY_ITEMS } from '@/constants/types';
import { useDispatch } from 'react-redux';
import { setSortingFilters } from '@/redux/reducers/products-reducer';
import Button from '../Button';
import Icon from '../Icon';
import TextSmall from '../Typography/TextSmall';
import TextSpan from '../Typography/TextSpan';
import TextLarge from '../Typography/TextLarge';
import SortLabel from './components/SortLabel';

function CategorySort({ data = [], className = '', onSort }) {
  const dispatch = useDispatch();
  const categorySortRef = useRef(null);
  const [openSortMenu, setOpenSortMenu] = useState(false);
  const [translateY, setTranslateY] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const [activeItem, setActiveItem] = useState(SORT_BY_ITEMS[0]);
  const [activeUnformattedItem, setActiveUnformattedItem] = useState(
    SORT_BY_ITEMS[0].name,
  );

  const toggleSortMenu = () => {
    setOpenSortMenu(!openSortMenu);
  };

  const handleSelectItem = (item) => {
    const formattedName = item?.name; // formatString(item?.name);
    setActiveItem(item);
    setActiveUnformattedItem(formattedName);
    setOpenSortMenu(false);
  };

  const handleTranslateY = (value) => {
    setTranslateY(value);
  };

  const handleClose = (state) => {
    if (state === SWIPE) {
      setIsClosing(true);
      setTimeout(() => {
        toggleSortMenu();
        setTranslateY(0);
        setIsClosing(false);
      }, 200);
    } else {
      toggleSortMenu();
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        categorySortRef.current &&
        !categorySortRef.current.contains(event.target)
      ) {
        toggleSortMenu();
      }
    };

    if (openSortMenu) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [openSortMenu]);

  useEffect(() => {
    if (data.length > 0) {
      setActiveUnformattedItem(data[0].name);
    }
  }, [data]);

  useEffect(() => {
    if (onSort) {
      onSort({ order_by: activeItem?.key });
    } else {
      // TODO: else will remove when this will use globally
      dispatch(setSortingFilters({ order_by: activeItem?.key }));
    }
  }, [activeItem]);

  return (
    <div
      ref={categorySortRef}
      className={cn('category-sort relative', className)}
    >
      <Button
        type="outline"
        className={cn(
          '!h-[32px] lg:!h-[40px] flex justify-center items-center gap-[4px] bg-white py-4 !px-12 lg:!px-12 border !border-himalayan-poppy rounded-[100px] focus:!border-2',
          {
            '!border-2': openSortMenu,
          },
          className,
        )}
        onClick={toggleSortMenu}
      >
        <TextSpan
          text="Sort by:"
          className="text-xss lg:text-xs text-senate text-left font-medium whitespace-nowrap"
        />

        <TextSmall
          text={String(activeUnformattedItem || '')}
          className="text-senate text-left lg:leading-5 font-semibold inline-block line-clamp-1 capitalize"
        />

        <Icon
          type="angle-down2"
          className={cn(
            'size-[12px] lg:size-[20px] transition-all duration-200',
            {
              'rotate-180': openSortMenu,
            },
          )}
        />
      </Button>

      {/* category sort menu */}
      <div
        className={cn(
          'category-sort-menu w-full h-full lg:h-max bg-black/30 invisible transition-all duration-100 opacity-30 z-[513] fixed lg:static inset-0',
          {
            '!visible !opacity-100': openSortMenu,
          },
        )}
      >
        <div
          style={{
            transform: `translateY(${translateY}px)`,
            transition: isClosing
              ? 'transform 0.3s ease-out, opacity 0.3s ease-out'
              : 'transform 0.3s ease-out',
          }}
          className={cn(
            'bg-white lg:w-[360px] lg:mt-6 p-20 fixed lg:absolute -bottom-[440px] lg:bottom-auto left-0 right-0 lg:right-[8px] lg:left-auto lg:top-full lg:rounded-xl opacity-0 rounded-t-xl transition-all duration-[400ms] shadow-category-sort-by2',
            {
              '!bottom-0 lg:!bottom-auto z-[20] !opacity-100': openSortMenu,
            },
          )}
        >
          <Button
            type="modal-swipe-close-button"
            onTranslateY={handleTranslateY}
            onClose={handleClose}
          />
          <div className="flex items-center justify-between gap-[10px] pt-16 lg:pt-0 pb-16">
            <TextLarge
              text="Sort by"
              className="text-lg font-bold text-coarse-wool"
            />

            <Icon
              type="close"
              className="block lg:hidden cursor-pointer"
              onClick={toggleSortMenu}
            />
          </div>

          <ul>
            {data && data.length > 0
              ? data.map((item, index) => {
                  const formattedName = item?.name; // formatString(item?.name);
                  const isLastItem = index === data.length - 1;

                  return (
                    <li
                      role="presentation"
                      key={item.id}
                      className={cn(
                        'flex justify-between items-center py-12 lg:py-16 px-8 cursor-pointer',
                        {
                          'border-b border-aquarelle-purple': !isLastItem,
                        },
                      )}
                      onClick={() => handleSelectItem(item)}
                    >
                      <SortLabel
                        activeItem={activeItem}
                        formattedName={formattedName}
                        item={item}
                      />
                    </li>
                  );
                })
              : SORT_BY_ITEMS.map((item, index) => {
                  const formattedName = item?.name; // formatString(item?.name);
                  const isLastItem = index === SORT_BY_ITEMS.length - 1;

                  return (
                    <li
                      role="presentation"
                      key={item?.id}
                      className={cn(
                        'flex justify-between items-center py-12 lg:py-16 px-8 cursor-pointer',
                        {
                          'border-b border-aquarelle-purple': !isLastItem,
                        },
                      )}
                      onClick={() => handleSelectItem(item)}
                    >
                      <SortLabel
                        activeItem={activeItem}
                        formattedName={formattedName}
                        item={item}
                      />
                    </li>
                  );
                })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CategorySort;
